/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { UntypedFormGroup } from '@angular/forms';
import * as strings from '@touchplan/strings';
import {
	ActualPhase,
	Location,
	MilestoneType,
	Priority,
	ProjectMember,
	Role,
	TicketType,
	Variance,
} from '@touchplan/common';
import { filter, map, takeUntil } from 'rxjs/operators';
import { Observable, Subject } from 'rxjs';
import { CalendarWorkdays, GroupedTicket } from '@touchplan/ticket';
import {
	TicketTypeCombined,
	TicketTypeUpdateStatus,
} from 'src/app/app-data/models/ticket-type/TicketTypeExtensions';
import { hasId } from 'src/app/common/fancy-firebase/generics/interfaces';
import ticketValidator from '@touchplan/config/lib/ticket-validators.json';
import { DynamicFlagTypes } from '@touchplan/feature-flags';
import { FeatureFlagManager } from 'src/app/services/FeatureFlagManager';
import { GranularEnabledService } from '../../../granular-time/granular-enabled.service';

@Component({
	selector: 'ticket-edit-form',
	templateUrl: './ticket-edit-form.component.html',
	styleUrls: ['./ticket-edit-form.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TicketEditFormComponent implements OnInit, OnDestroy {
	@Input({ required: true }) public ticketForm!: UntypedFormGroup;
	@Input({ required: true }) public editTicket$!: Observable<GroupedTicket>;
	@Input() public roleList$: Observable<Role[]> | undefined;
	@Input() public locationList$: Observable<Location[]> | undefined;
	@Input() public projectMembers$: Observable<ProjectMember[]> | undefined;
	@Input() public priorityList$: Observable<Priority[]> | undefined;
	@Input() public varianceList$: Observable<Variance[]> | undefined;
	@Input() public activeLineDate$: Observable<string> | undefined;
	@Input() public isAdmin$: Observable<boolean> | undefined;
	// Only pass if trying to override existing form styles.
	@Input() public classOverrides?: TicketTypeCombined;
	@Input({ required: true }) public calendarWorkdays!: CalendarWorkdays;

	protected responsibleProjectMember$!: Observable<ProjectMember | undefined>;

	public milestoneTypes = [
		{ label: 'Finish', value: MilestoneType.FINISH },
		{ label: 'Start', value: MilestoneType.START },
	];

	public appStrings = strings;
	public validationData = ticketValidator;

	public mobileStrings = {
		ENTER_VALID_DATE_ERR: strings.ENTER_VALID_DATE_ERR,
		START_DATE_BIGGER_THEN_FINISH_DATE_ERR: strings.START_DATE_BIGGER_THEN_FINISH_DATE_ERR,
		FINISH_DATE_BIGGER_THEN_ACTIVE_LINE_DATE: strings.FINISH_DATE_BIGGER_THEN_ACTIVE_LINE_DATE,
	};
	public ticketPhase: ActualPhase | undefined;
	public ticketType = 'task';
	public milestoneType: MilestoneType | undefined;
	public showVarianceReason = false;
	public liveStartValue$!: Observable<string | undefined>;

	public isTicketWbsEnabled$ = this.featureFlagManager.getFlag$(DynamicFlagTypes.TICKETS_WBS_ENABLED);
	public isGTPEnabled$ = this.granularEnabledService.isGranularEnabled$;
	public ticketTypes = TicketType;

	private shutdownSubject$: Subject<boolean> = new Subject();
	constructor(
		private featureFlagManager: FeatureFlagManager,
		private granularEnabledService: GranularEnabledService
	) {}

	public ngOnInit() {
		this.editTicket$.pipe(takeUntil(this.shutdownSubject$)).subscribe((ticket) => {
			if (ticket) {
				this.ticketType = ticket.view.type ? ticket.view.type : 'task';
				this.milestoneType = ticket.view.milestoneType;
				this.ticketPhase = ticket.view.actualPhase;
				this.showVarianceReason = !!ticket.view.needsVariance;
			}
		});

		this.responsibleProjectMember$ = this.editTicket$.pipe(
			map((ticket) => ticket?.view.responsibleProjectMember),
			takeUntil(this.shutdownSubject$)
		);

		// Get the initial and updated 'liveStart' value.
		this.liveStartValue$ = this.editTicket$.pipe(map((t) => t.view.liveStart));
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-7217
	// Find more elegant solution for these labels

	/**
	 * Returns the correct label for the start calendar field depending on the ticket type
	 * @return string to display as the label.
	 */
	public startCalendarLabel(isGtpEnabled: boolean): string {
		if (this.isUpdateStatusDialog()) {
			return this.appStrings.START_ACTUAL;
		} else if (isGtpEnabled) {
			if (this.ticketPhase === ActualPhase.planned || this.ticketPhase === ActualPhase.plannedPreview) {
				return this.appStrings.PLANNED_START_DATE;
			} else {
				return this.appStrings.ACTUAL_START_DATE;
			}
		} else {
			return this.appStrings.START;
		}
	}

	/**
	 * Gets the correct label for the finish calendar field depending on the ticket type.
	 * @return string to display as the label.
	 */
	public finishCalendarLabel(isGtpEnabled: boolean) {
		switch (this.ticketType) {
			case TicketType.Constraint:
				return this.appStrings.PLANNED_DATE;
			case TicketType.Milestone: {
				return this.getMilestoneCalendarLabel(isGtpEnabled);
			}
			default:
				if (this.isUpdateStatusDialog()) {
					return this.appStrings.FINISH_ACTUAL;
				} else if (isGtpEnabled) {
					if (this.ticketPhase === ActualPhase.planned || this.ticketPhase === ActualPhase.plannedPreview) {
						return this.appStrings.PLANNED_FINISH_DATE;
					} else {
						return this.appStrings.ACTUAL_FINISH_DATE;
					}
				} else {
					return this.appStrings.FINISH;
				}
		}
	}

	/**
	 * Gets the correct label for the finish calendar field for a milestone ticket.
	 * @returns string to display as the label.
	 * @private
	 */
	private getMilestoneCalendarLabel(isGtpEnabled: boolean): string {
		if (isGtpEnabled) {
			const isPlanned =
				this.ticketPhase === ActualPhase.planned || this.ticketPhase === ActualPhase.plannedPreview;
			if (this.milestoneType === MilestoneType.START) {
				return isPlanned ? this.appStrings.PLANNED_START_DATE : this.appStrings.ACTUAL_START_DATE;
			} else if (this.milestoneType === MilestoneType.FINISH) {
				return isPlanned ? this.appStrings.PLANNED_FINISH_DATE : this.appStrings.ACTUAL_FINISH_DATE;
			} else {
				return this.appStrings.DATE;
			}
		} else {
			if (this.milestoneType === MilestoneType.START) {
				return this.appStrings.START_ACTUAL;
			} else if (this.milestoneType === MilestoneType.FINISH) {
				return this.appStrings.FINISH_ACTUAL;
			} else {
				return this.appStrings.DATE_ACTUAL;
			}
		}
	}

	/**
	 * Returns the correct label for the finish time field depending on the state of what type of ticket.
	 * @return string the label to show on the finish time field.
	 */
	public startTimeLabel(): string {
		switch (this.ticketType) {
			case TicketType.Task:
				if (this.ticketPhase === ActualPhase.planned || this.ticketPhase === ActualPhase.plannedPreview) {
					return this.appStrings.PLANNED_START_TIME;
				} else {
					return this.appStrings.ACTUAL_START_TIME;
				}
			default:
				return this.appStrings.PLANNED_START_TIME;
		}
	}

	/**
	 * Returns the correct label for the finish time field depending on the state of what type of ticket.
	 * @return string the label to show on the finish time field.
	 */
	public finishTimeLabel(): string {
		const isPlanned =
			this.ticketPhase === ActualPhase.planned || this.ticketPhase === ActualPhase.plannedPreview;
		switch (this.ticketType) {
			case TicketType.Task:
				return isPlanned ? this.appStrings.PLANNED_FINISH_TIME : this.appStrings.ACTUAL_FINISH_TIME;
			case TicketType.Milestone: {
				if (this.milestoneType === MilestoneType.START) {
					return isPlanned ? this.appStrings.PLANNED_START_TIME : this.appStrings.ACTUAL_START_TIME;
				} else if (this.milestoneType === MilestoneType.FINISH) {
					return isPlanned ? this.appStrings.PLANNED_FINISH_TIME : this.appStrings.ACTUAL_FINISH_TIME;
				} else {
					return this.appStrings.TIME;
				}
			}
			case TicketType.Constraint: {
				return this.appStrings.TIME;
			}
			default:
				return this.appStrings.PLANNED_FINISH_TIME;
		}
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-7217

	private isUpdateStatusDialog(): boolean {
		return (
			!!this.classOverrides &&
			(this.classOverrides === TicketTypeUpdateStatus.TaskUpdateStatusDialogForMobile ||
				this.classOverrides === TicketTypeUpdateStatus.MilestoneUpdateStatusDialogForMobile ||
				this.classOverrides === TicketTypeUpdateStatus.TaskUpdateStatusDialogForDesktop ||
				this.classOverrides === TicketTypeUpdateStatus.MilestoneUpdateStatusDialogForDesktop ||
				this.classOverrides === TicketTypeUpdateStatus.ConstraintUpdateStatusDialog)
		);
	}

	public defaultSplitDataSelectCompareFunc(optionValue: hasId, selectedValue: hasId) {
		return optionValue?.$id === selectedValue?.$id;
	}

	private disableStartDateOnTicketChanges() {
		// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6142
		// The code below is correct, but placeholder tags were added to verify that the issue reported by TP-6142 is fixed.
		// Once it was fixed, the placeholder tags should be removed.
		// It seems this was an intended code to solve the issue described in TP-6142, but it was causing the start date to get disabled on unintended
		// phases of the ticket. Disabling this code for now, but it should be revisited.
		this.editTicket$
			.pipe(
				filter((ticket) => ticket.view.type === TicketType.Task || !ticket.view.type),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe((ticket) => {
				if (
					ticket.view.actualPhase === ActualPhase.started ||
					ticket.view.previousActualPhase === ActualPhase.started
				) {
					this.ticketForm.get('liveStart')?.disable({ emitEvent: false });
				} else {
					this.ticketForm.get('liveStart')?.enable({ emitEvent: false });
				}
			});
		// END PLACEHOLDER REPLACE AS PART OF TP-6142
	}

	public ngOnDestroy() {
		this.shutdownSubject$.next(true);
		this.shutdownSubject$.complete();
	}
}
